<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{
  message?: string;
  /** CMS bootstrap failed — show message without spinner */
  error?: boolean;
}>();

const displayMessage = computed(() => {
  if (props.error) return props.message ?? 'Something went wrong';
  return props.message ?? 'Loading…';
});
</script>

<template>
  <div
    class="loading-app"
    :class="{ 'loading-app--error': error }"
    :role="error ? 'alert' : 'status'"
    :aria-busy="error ? undefined : true"
    aria-live="polite"
  >
    <span v-if="!error" class="loading-app__spinner" aria-hidden="true" />
    <p class="loading-app__text">{{ displayMessage }}</p>
  </div>
</template>

<style scoped>
.loading-app {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  box-sizing: border-box;
  min-height: 100vh;
  width: 100%;
  padding: 2rem 1.5rem;
  background-color: var(--color-bg);
  font-family: inherit;
}

.loading-app__spinner {
  width: 2rem;
  height: 2rem;
  border: 2px solid var(--color-border);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: loading-app-spin 0.75s linear infinite;
}

.loading-app__text {
  margin: 0;
  max-width: 36rem;
  text-align: center;
  font-size: 0.9375rem;
  font-weight: 500;
  line-height: 1.5;
  color: var(--color-text-muted);
}

.loading-app--error .loading-app__text {
  color: var(--color-error);
  font-weight: 500;
}

@keyframes loading-app-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .loading-app__spinner {
    animation: none;
    border-top-color: var(--color-border);
    opacity: 0.85;
  }
}
</style>
